<template>
  <c-modal
    v-model:open="open"
    :class="b()"
    :content-class="b(formModifiers)"
    size="500"
  >
    <template v-if="showConfirmation">
      <h2 :class="b('title')">
        {{ matchThemeTranslation(themeConfig.translations.titleConfirmation) }}
      </h2>
      <p>
        {{ matchThemeTranslation(themeConfig.translations.textConfirmationMessage) }}
      </p>
      <div :class="b('close-button-wrapper')">
        <e-button
          :class="b('close-button')"
          :width="$viewport.isSm ? 'auto' : 'full'"
          variant="primary"
          spacing="600"
          @click="close"
        >
          {{ matchThemeTranslation(themeConfig.translations.buttonLabelClose) }}
        </e-button>
      </div>
    </template>

    <template v-else>
      <h2
        ref="headline"
        :class="b('title')"
      >
        {{ matchThemeTranslation(themeConfig.translations.title) }}
      </h2>
      <p :class="b('description')">
        {{ matchThemeTranslation(themeConfig.translations.description) }}
      </p>
      <form
        :class="b('form', formModifiers)"
        novalidate
        @submit.prevent="onSubmit"
      >
        <div :class="b('radio-row')">
          <e-radio
            v-model="subscribeToVersions"
            :class="b('radio-group')"
            :value="1"
            name="subscribeTo"
          >
            {{ matchThemeTranslation(themeConfig.translations.radioLabelSubscribe) }}
          </e-radio>
        </div>
        <div :class="b('radio-row')">
          <e-radio
            v-model="subscribeToVersions"
            :class="b('radio-group')"
            :value="0"
            name="subscribeTo"
          >
            {{ matchThemeTranslation(themeConfig.translations.radioLabelDownload) }}
          </e-radio>
        </div>
        <div :class="b('form-body', { isHidden: subscribeToVersions === 0 })">
          <p :class="b('form-title')">
            {{ matchThemeTranslation(themeConfig.translations.formHeader) }}
          </p>
          <div
            v-if="isTheme('sagemax')"
            :class="b('form-row', { empty: true })"
          ></div>
          <div :class="b('form-row')">
            <component
              v-model="formData.email"
              :is="matchTheme(themeConfig.controls.eInput)"
              control="e-input"
              :label="`${matchThemeTranslation(themeConfig.translations.inputLabelEmail)}*`"
              :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderEmail)"
              :state="v$.formData.email.$error ? 'error' : 'default'"
              type="email"
              autocomplete="email"
              name="email"
            >
              <template v-if="v$.formData.email.$error">
                {{ matchThemeTranslation(themeConfig.translations.errorNotificationEmail) }}
              </template>
            </component>
          </div>
          <div :class="b('form-row', { terms: true })">
            <e-checkbox
              v-model="formData.acceptGeneralTermsOfUse"
              :state="v$.formData.acceptGeneralTermsOfUse.$error ? 'error' : 'default'"
              name="acceptGeneralTermsOfUse"
            >
              <i18n-t
                :keypath="matchTheme(themeConfig.translations.inputLabelGeneralTermsOfUse)"
                scope="global"
              >
                <template #link>
                  <a
                    :href="sessionStore.urls.generalTermsOfUsePage"
                    target="_blank"
                    rel="noopener noreferrer"
                    :class="b('terms-link')"
                  >
                    {{ matchThemeTranslation(themeConfig.translations.inputLabelGeneralTermsOfUseLink) }}*
                  </a>
                </template>
              </i18n-t>
            </e-checkbox>
          </div>
        </div>
        <div :class="b('form-footer')">
          <button
            v-if="isTheme('default')"
            :class="b('button-back')"
            type="button"
            @click="close"
          >
            <e-icon-sprite
              icon="i-chevron-1--left"
              size="22"
            />
            {{ matchThemeTranslation(themeConfig.translations.buttonLabelBack) }}
          </button>
          <e-button
            v-if="!subscribeToVersions && !!eifuStore.downloadViewFile"
            :class="b('button-submit')"
            :width="$viewport.isSm ? 'auto' : 'full'"
            variant="primary"
            type="button"
            spacing="600"
            @click="downloadFile(false)"
          >
            {{ matchThemeTranslation(themeConfig.translations.buttonLabelDownload) }}
          </e-button>
          <e-button
            v-if="subscribeToVersions"
            :class="b('button-submit')"
            :width="$viewport.isSm ? 'auto' : 'full'"
            variant="primary"
            type="submit"
            spacing="600"
          >
            {{ matchThemeTranslation(themeConfig.translations.buttonLabelSubscribeAndDownload) }}
          </e-button>
        </div>
      </form>
    </template>
  </c-modal>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core';
  import { email, required } from '@vuelidate/validators';
  import useEifuStore from '@/stores/eifu';
  import useSessionStore from '@/stores/session';
  import cModal from '@/components/c-modal.vue';
  import eButton from '@/elements/e-button.vue';
  import eCheckbox from '@/elements/e-checkbox.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eRadio from '@/elements/e-radio.vue';
  import downloadFileByUrl from '@/helpers/download-file.js';
  import themes from '@/mixins/themes.js';

  /**
   * Renders an electronic instruction download modal.
   */
  export default {
    name: 'c-electronic-instruction-download',
    components: {
      eIconSprite,
      eCheckbox,
      eRadio,
      eButton,
      cModal,
    },
    mixins: [themes],
    // props: {},

    setup() {
      return {
        v$: useVuelidate(),
        eifuStore: useEifuStore(),
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Number} Holds the subscription or download selection.
         */
        subscribeToVersions: 0,

        /**
         * @type {Boolean} Defines if the confirmation should be displayed.
         */
        showConfirmation: false,

        /**
         * @type {Object} Holds the form data.
         */
        formData: {
          email: '',
          acceptGeneralTermsOfUse: false,
        },

        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          controls: {
            eInput: {
              default: 'e-input',
              sagemax: 'c-labeled-control',
            },
          },
          translations: {
            titleConfirmation: {
              default: 'c-electronic-instruction-download.titleConfirmation',
              sagemax: 'c-sagemax-electronic-instruction-download.titleConfirmation',
            },
            textConfirmationMessage: {
              default: 'c-electronic-instruction-download.textConfirmationMessage',
              sagemax: 'c-sagemax-electronic-instruction-download.textConfirmationMessage',
            },
            buttonLabelClose: {
              default: 'c-electronic-instruction-download.buttonLabelClose',
              sagemax: 'c-sagemax-electronic-instruction-download.buttonLabelClose',
            },
            title: {
              default: 'c-electronic-instruction-download.title',
              sagemax: 'c-sagemax-electronic-instruction-download.title',
            },
            description: {
              default: 'c-electronic-instruction-download.description',
              sagemax: 'c-sagemax-electronic-instruction-download.description',
            },
            radioLabelSubscribe: {
              default: 'c-electronic-instruction-download.radioLabelSubscribe',
              sagemax: 'c-sagemax-electronic-instruction-download.radioLabelSubscribe',
            },
            radioLabelDownload: {
              default: 'c-electronic-instruction-download.radioLabelDownload',
              sagemax: 'c-sagemax-electronic-instruction-download.radioLabelDownload',
            },
            formHeader: {
              default: 'c-electronic-instruction-download.formHeader',
              sagemax: 'c-sagemax-electronic-instruction-download.formHeader',
            },
            inputLabelEmail: {
              default: 'c-electronic-instruction-download.inputLabelEmail',
              sagemax: 'c-sagemax-electronic-instruction-download.inputLabelEmail',
            },
            inputPlaceholderEmail: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-download.inputPlaceholderEmail',
            },
            errorNotificationEmail: {
              default: 'c-electronic-instruction-download.errorNotificationEmail',
              sagemax: 'c-sagemax-electronic-instruction-download.errorNotificationEmail',
            },
            inputLabelGeneralTermsOfUse: {
              default: 'c-electronic-instruction-download.inputLabelGeneralTermsOfUse',
              sagemax: 'c-sagemax-electronic-instruction-download.inputLabelGeneralTermsOfUse',
            },
            inputLabelGeneralTermsOfUseLink: {
              default: 'c-electronic-instruction-download.inputLabelGeneralTermsOfUseLink',
              sagemax: 'c-sagemax-electronic-instruction-download.inputLabelGeneralTermsOfUseLink',
            },
            buttonLabelBack: {
              default: 'c-electronic-instruction-download.buttonLabelBack',
              sagemax: 'c-sagemax-electronic-instruction-download.buttonLabelBack',
            },
            buttonLabelDownload: {
              default: 'c-electronic-instruction-download.buttonLabelDownload',
              sagemax: 'c-sagemax-electronic-instruction-download.buttonLabelDownload',
            },
            buttonLabelSubscribeAndDownload: {
              default: 'c-electronic-instruction-download.buttonLabelSubscribeAndDownload',
              sagemax: 'c-sagemax-electronic-instruction-download.buttonLabelSubscribeAndDownload',
            },
          },
          bannerImage: {
            imageInitialHeight: {
              default: 1200,
              sagemax: 470,
            },
            position: {
              default: 'right',
              sagemax: 'top',
            },
          },
        },
      };
    },

    /**
     * Vuelidate configuration.
     */
    validations: {
      formData: {
        email: {
          required,
          email,
        },
        acceptGeneralTermsOfUse: {
          checked: (value) => !!value,
        },
      },
    },

    computed: {
      /**
       * Handles the open state of the modal.
       */
      open: {
        get() {
          return !!this.eifuStore.downloadViewFile;
        },
        set(open) {
          if (!open) {
            this.eifuStore.downloadViewFile = null;
          }
        },
      },

      /**
       * Returns a definition of BEM modifiers.
       *
       * @returns {Object}
       */
      formModifiers() {
        const { theme } = this;

        return {
          theme,
        };
      },
    },
    watch: {
      /**
       * Checks for changes and resets the form.
       */
      'subscribeToVersions': {
        handler() {
          this.resetForm();
        },
      },

      /**
       * Checks for change of the download file.
       */
      'eifuStore.downloadViewFile': {
        handler() {
          this.resetForm();
          this.subscribeToVersions = 0;
        },
        deep: true,
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Closes the modal.
       */
      close() {
        this.showConfirmation = false;
        this.open = false;
      },

      /**
       * Reset form data and states.
       */
      resetForm() {
        this.formData.email = '';
        this.formData.acceptGeneralTermsOfUse = false;
        this.v$.$reset();
      },

      /**
       * Form submit handler.
       */
      onSubmit() {
        this.v$.$touch();

        if (this.v$.$pending || this.v$.$error) {
          return;
        }

        const { fileId, version, brandNames, referenceNumbers, udiDiNumbers } = this.eifuStore.downloadViewFile || {};
        const { formData } = this;

        const payload = {
          ...formData,
          id: fileId,
          celumVersion: version || null,
          brandNames,
          referenceNumbers,
          udiDiNumbers,
          locale: this.sessionStore.locale,
        };

        this.eifuStore
          .apiSubscribeToAssetVersionUpdates({ assetId: fileId, formData: payload })
          .then(
            () => {
              // Handle the success case.
              this.showConfirmation = true;
            },
            () => {
              // Handle the error case.
              const headlineRef = this.$refs.headline?.closest('.vm--container');

              if (headlineRef) {
                headlineRef.scrollTop = 0;
              }
            },
          )
          .finally(() => {
            this.downloadFile(true);
          });
      },

      /**
       * Downloads the file, resets and closes the modal.
       *
       * @param {Boolean} remainOpenAfterDownload - Defines if the modal should be left open after the download.
       */
      downloadFile(remainOpenAfterDownload) {
        downloadFileByUrl(this.eifuStore.downloadViewFile.fileName, this.eifuStore.downloadViewFile.fileUrl);
        this.v$.$reset();

        if (!remainOpenAfterDownload) {
          this.close();
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-electronic-instruction-download {
    $this: &;

    &--theme-sagemax {
      #{$this}__description {
        font-weight: variables.$font-weight--regular;

        @include mixins.media(md) {
          margin-bottom: functions.rem(variables.$spacing--40);
        }
      }

      #{$this}__title {
        margin-bottom: functions.calc-rem(variables.$spacing--40);
      }

      #{$this}__form-title {
        margin-bottom: functions.calc-rem(variables.$spacing--5);
        font-weight: variables.$font-weight--regular;
      }

      #{$this}__form-body {
        margin-top: functions.calc-rem(variables.$spacing--5);
      }

      #{$this}__form-footer {
        justify-content: flex-end;
        margin-top: 0;
      }

      #{$this}__form-row {
        &--terms {
          margin-top: 0;
        }

        &:last-of-type {
          // this case is the exception of the rule because the form footer is outside the other form_rows
          // so the !important is needed to override default rule
          border-bottom: none !important; // stylelint-disable-line declaration-no-important
        }
      }
    }

    &__title {
      @extend %hyphens;

      margin-bottom: functions.rem(variables.$spacing--25);

      @include mixins.media(md) {
        margin-bottom: functions.rem(variables.$spacing--30);
      }
    }

    &__description {
      margin-bottom: functions.rem(variables.$spacing--25);
      font-weight: variables.$font-weight--semi-bold;
    }

    &__radio-row {
      margin-bottom: functions.rem(variables.$spacing--15);
    }

    &__form {
      &--theme-sagemax {
        #{$this}__terms-link {
          font-weight: variables.$font-weight--semi-bold;
          text-decoration: none;
        }
      }
    }

    &__form-body {
      position: relative;
      margin-top: functions.rem(variables.$spacing--50);

      &--is-hidden {
        &::before {
          @include mixins.z-index(front);

          position: absolute;
          top: 0;
          left: 0;
          content: '';
          width: 100%;
          height: 100%;
          background: rgba(variables.$color-grayscale--1000, 0.6);
        }
      }
    }

    &__form-title {
      margin-bottom: functions.rem(variables.$spacing--30);
      font-weight: variables.$font-weight--semi-bold;
    }

    &__form-row {
      max-width: functions.rem(900px);

      &--terms {
        margin-top: functions.rem(variables.$spacing--25);
      }
    }

    &__form-footer {
      display: flex;
      flex-wrap: wrap;
      margin-top: functions.rem(variables.$spacing--40);

      @include mixins.media(sm) {
        flex-wrap: nowrap;
        justify-content: space-between;
      }

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$spacing--60);
      }
    }

    &__button-submit {
      order: 0;
      margin-bottom: functions.rem(variables.$spacing--30);

      @include mixins.media(sm) {
        order: 1;
        margin-bottom: 0;
      }
    }

    &__button-back {
      @extend %button-reset;
      @extend %link--primary-2;

      display: flex;
      align-items: center;
      order: 1;

      .e-icon-sprite {
        width: functions.rem(22px);
        height: functions.rem(22px);
        margin-right: functions.rem(variables.$spacing--10);
      }
    }

    &__close-button-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    &__close-button {
      margin-top: functions.rem(variables.$spacing--40);
    }
  }
</style>
